import { autoUpdate, size, useFloating } from '@floating-ui/react-dom';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import _ from 'lodash';
import Quill from 'quill/core';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useThrottledCallback } from 'use-debounce';
import useAppSelector from '../app/redux/useAppSelector';
import Button from '../components/Button';
import IconButton from '../components/IconButton';
import SpringAnimation from '../components/SpringAnimation';
import Tooltip from '../components/Tooltip';
import PopOverContainer from '../components/deprecated/PopOverContainer';
import { useQuill } from '../editor/QuillContext';
import tocIcon from '../icons/non-prefilled/16x16/tableOfContentsNew.svg';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import useEventListener from '../utils/useEventListener';
import {
  usePostLayoutSize,
  usePostLayoutState,
  useToggleTOC,
} from './PostLayoutProvider';
import styles from './PostStickyControl.module.scss';
import type { TOCState } from './actionButtons/ConnectedTableOfContents';
import ConnectedTableOfContents from './actionButtons/ConnectedTableOfContents';
import TableOfContents from './actionButtons/TableOfContents';
import {
  getScrollContainer,
  useListenToPostScroll,
} from './listenToPostScroll';
import useIsPublicView from '../utils/useIsPublicView';

const ACTIONS_TOP_OFFSET = parseInt(styles.postActionsTopOffset, 10);
const TOP_OFFSET_WITHOUT_STATEBAR = parseInt(
  styles.topOffsetWithoutStateBar,
  10,
);

const tallerThanEditor = (quill: Quill, offset: number) => {
  let lastVisibleElementChild = quill.root.lastElementChild;
  while (lastVisibleElementChild instanceof HTMLElement) {
    if (lastVisibleElementChild.offsetParent) {
      return (
        offset >
        lastVisibleElementChild.offsetTop + lastVisibleElementChild.offsetHeight
      );
    }

    lastVisibleElementChild = lastVisibleElementChild.previousElementSibling;
  }

  return false;
};

const useElementHeightObserver = (
  element: HTMLElement | null | undefined,
  setHeight: (height: number) => void,
) => {
  useEffect(() => {
    setHeight(element?.offsetHeight ?? 0);
    if (!element) return undefined;

    const resizeObserver = new ResizeObserver((entries) => {
      const { height } = entries[0]?.contentRect || {};
      if (typeof height === 'number') {
        setHeight(height);
      }
    });
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [element, setHeight]);
};

type WiderLineRange = {
  top: number;
  bottom: number;
  left: number;
};

const useWiderLineRanges = (containerRef: React.RefObject<HTMLElement>) => {
  const { quill } = useQuill();
  const [ranges, setRanges] = useState<WiderLineRange[]>([]);
  const { callback: throttledUpdateRange, flush } = useThrottledCallback(
    () => {
      const newRanges = quill
        ? Array.from(quill.container.querySelectorAll('.span-full')).flatMap(
            (node) => {
              if (!(node instanceof HTMLElement) || !node.offsetParent) {
                return [];
              }
              const rect = node.getBoundingClientRect();
              const top = node.offsetTop;
              return [
                {
                  left: rect.left,
                  top,
                  bottom: top + node.offsetHeight,
                },
              ];
            },
          )
        : [];

      if (!_.isEqual(ranges, newRanges)) {
        setRanges(newRanges);
      }
    },
    200,
    { leading: false },
  );

  const forceUpdateRange = useCallback(() => {
    throttledUpdateRange();
    flush();
  }, [throttledUpdateRange, flush]);

  // Images maybe resized after loaded.
  useElementHeightObserver(quill?.root, forceUpdateRange);

  // When the container resizes, the wider lines might not be in the way
  // of the sticky control anymore, therefore we need to recalculate
  useEffect(() => {
    if (!containerRef.current) return undefined;
    const resizeObserver = new ResizeObserver((entries) => {
      const { width } = entries[0]?.contentRect || {};
      if (width) {
        forceUpdateRange();
      }
    });
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [containerRef, forceUpdateRange]);

  useEffect(() => {
    if (!quill) return undefined;
    quill.on(Quill.events.TEXT_CHANGE, throttledUpdateRange);
    return () => {
      quill.off(Quill.events.TEXT_CHANGE, throttledUpdateRange);
    };
  }, [quill, throttledUpdateRange]);

  return ranges;
};

interface TocButtonProps {
  onClick: () => void;
  ariaLabel: string;
  hasHeaders: boolean;
}

const TocButton = React.forwardRef<HTMLButtonElement, TocButtonProps>(
  ({ onClick, ariaLabel, hasHeaders }, ref) => {
    const isDisabled =
      useAppSelector((state) => state.editor.editable) === false && !hasHeaders;

    const button = (
      <IconButton
        ref={ref}
        onClick={onClick}
        aria-label={ariaLabel}
        className={styles.tocButton}
        disabled={isDisabled}
      >
        {tocIcon}
      </IconButton>
    );

    if (hasHeaders || isDisabled) {
      return <>{button}</>;
    }

    return (
      <Tooltip
        content={
          'Add headings to your post\nto automatically add\na table of contents'
        }
        placement="right"
        trigger="clickOrHover"
      >
        {button}
      </Tooltip>
    );
  },
);

interface ScrollableContentContainerProps {
  children: React.ReactNode;
}

const ScrollableContentContainer = ({
  children,
}: ScrollableContentContainerProps) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  useEventListener(container, 'scroll', () => {
    container?.classList.toggle(styles.scrolled, container.scrollTop > 0);
  });

  return (
    <div ref={setContainer} className={styles.content}>
      {children}
    </div>
  );
};

const useAnimateMarginTop = (tocRef: React.RefObject<HTMLDivElement>) => {
  const lastMarginTopRef = useRef<number>(0);
  const animationRef = useRef<Animation | null>(null);

  return useCallback(
    async (marginTop: number, isStickingFromBelow?: boolean) => {
      // we don't need to animate if the argument didn't change compared to the last call
      if (!tocRef.current || lastMarginTopRef.current === marginTop) return;
      lastMarginTopRef.current = marginTop;

      const from = getComputedStyle(tocRef.current).marginTop;
      const to = marginTop + ACTIONS_TOP_OFFSET;

      // in case the TOC sticks to a wider line range from below
      // we want to immediately set the next value, otherwise
      // it would take a while until it animates
      const isImmediate =
        isStickingFromBelow && from === `${ACTIONS_TOP_OFFSET}px`;

      animationRef.current?.finish();
      const animation = tocRef.current.animate(
        [
          {
            marginTop: from,
            easing: 'ease-out',
          },
          {
            marginTop: `${to}px`,
            easing: 'ease-in',
          },
        ],
        { duration: isImmediate ? 0 : 300, fill: 'forwards' },
      );
      animationRef.current = animation;

      await animation.finished;
      animation.commitStyles();
      animation.cancel();

      if (animationRef.current === animation) {
        animationRef.current = null;
      }
    },
    [tocRef],
  );
};

interface TOCPopOverProps {
  open: boolean;
  style: React.CSSProperties;
  tocState: TOCState;
}

const TOCPopOver = React.forwardRef<HTMLDivElement, TOCPopOverProps>(
  ({ open, style, tocState }, ref) => {
    return (
      <div
        ref={ref}
        style={{
          ...style,
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        <AnimatePresence>
          {open && (
            <SpringAnimation
              direction="down"
              className={styles.tocPopOverContainer}
            >
              <PopOverContainer width="auto">
                <div className={styles.tocPopOverContent}>
                  <h4 className={styles.title}>Table of Contents</h4>
                  <TableOfContents {...tocState} />
                </div>
              </PopOverContainer>
            </SpringAnimation>
          )}
        </AnimatePresence>
      </div>
    );
  },
);

interface PostStickyControlProps {
  postId: string;
  postLayoutContainerRef: React.RefObject<HTMLElement>;
  postLayoutBannerRef: React.RefObject<HTMLDivElement>;
  hasBanner: boolean;
}

const PostStickyControl = ({
  postId,
  postLayoutContainerRef,
  postLayoutBannerRef,
  hasBanner,
}: PostStickyControlProps) => {
  const isPublicView = useIsPublicView();
  const [isTOCPopoverOpen, setIsTOCPopoverOpen] = useState(false);
  const [height, setHeight] = useState(0);
  const tocRef = useRef<HTMLDivElement>(null);
  const tocContainerRef = useRef<HTMLElement>(null);
  const { quill, readOnly } = useQuill();
  const toggleTOC = useToggleTOC();
  const { isTOCExpanded, canTOCExpand } = usePostLayoutState();
  const postLayoutSize = usePostLayoutSize();

  const widerLineRanges = useWiderLineRanges(postLayoutContainerRef);

  useElementHeightObserver(tocRef.current, setHeight);

  const middleware = useMemo(() => [size({ padding: 12 })], []);
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware,
  });

  useEffect(() => {
    if (!quill || postLayoutSize !== 'mobile') return undefined;
    const titleLine = quill.titleLine?.domNode;

    if (!(titleLine instanceof Element)) return undefined;

    if (!readOnly) {
      setIsTOCPopoverOpen(false);
      return undefined;
    }

    const onTitleClick = () => {
      setIsTOCPopoverOpen((isOpen) => !isOpen);
    };

    refs.setReference(titleLine);
    titleLine.addEventListener('click', onTitleClick);

    return () => {
      titleLine.removeEventListener('click', onTitleClick);
    };
  }, [quill, postLayoutSize, toggleTOC, readOnly, refs]);

  const animateMarginTop = useAnimateMarginTop(tocRef);

  const { callback: throttledUpdateStaticTop, flush } = useThrottledCallback(
    () => {
      if (!quill || !tocRef.current || height === 0) {
        animateMarginTop(0);
        return;
      }

      const bannerTop =
        postLayoutBannerRef.current?.getBoundingClientRect().top;
      const quillTop = quill.root.getBoundingClientRect().top;
      const editorTop = bannerTop ?? quillTop;
      const scrollContainerTop =
        getScrollContainer()?.getBoundingClientRect().top ?? 0;

      // widerLineRanges should be relative to the top including the banner
      const editorTopOffset = quillTop - editorTop;
      const ranges = widerLineRanges.map(({ top, bottom, ...rest }) => ({
        top: top + editorTopOffset,
        bottom: bottom + editorTopOffset,
        ...rest,
      }));

      const editorTopRelativeToScrollContainer = editorTop - scrollContainerTop;
      const relativeScrollPosition = Math.abs(
        Math.min(0, editorTopRelativeToScrollContainer),
      );

      const relativeTocTop = Math.max(
        TOP_OFFSET_WITHOUT_STATEBAR - editorTopRelativeToScrollContainer,
        0,
      );
      const relativeTocBottom = relativeTocTop + height;
      const rightBoundary = tocRef.current?.getBoundingClientRect().right ?? 0;
      const hasNoCollisionsWithWiderLineRanges = ranges.every(
        (range) =>
          range.left > rightBoundary ||
          relativeScrollPosition >= range.bottom ||
          relativeTocBottom <= range.top,
      );

      if (
        hasNoCollisionsWithWiderLineRanges &&
        // Sticky elements don't occupy space
        !tallerThanEditor(quill, relativeTocBottom)
      ) {
        animateMarginTop(0);
        return;
      }

      const rangesAfter = ranges.filter(
        ({ bottom }) => bottom >= relativeScrollPosition,
      );

      const targetRangeForStickyToc = rangesAfter.find((range, index) => {
        const top = range.bottom + ACTIONS_TOP_OFFSET;
        const next = rangesAfter[index + 1];
        return next ? top + height + ACTIONS_TOP_OFFSET <= next.top : true;
      });

      if (targetRangeForStickyToc) {
        const isStickingFromBelow =
          targetRangeForStickyToc.top <= relativeScrollPosition;
        animateMarginTop(
          targetRangeForStickyToc.bottom + ACTIONS_TOP_OFFSET,
          isStickingFromBelow,
        );
      }
    },
    100,
    { leading: false },
  );

  useListenToPostScroll(throttledUpdateStaticTop);

  useEffect(() => {
    flush();
    throttledUpdateStaticTop();
  }, [flush, throttledUpdateStaticTop, height, widerLineRanges]);

  return (
    <ConnectedTableOfContents postId={postId}>
      {(tocProps) => {
        const hasHeaders = tocProps.headers.length > 0;
        const tocButtonAriaLabel = isTOCExpanded
          ? 'Hide table of contents'
          : 'Show table of contents';

        if (postLayoutSize === 'mobile') {
          if (!hasHeaders) return null;

          return (
            <ClickOutsideHandler
              onClickOutside={(event) => {
                const titleLine = quill?.scroll.children.head?.domNode;
                if (!titleLine?.contains(event.target as Node)) {
                  setIsTOCPopoverOpen(false);
                }
              }}
            >
              <TOCPopOver
                ref={refs.setFloating}
                open={isTOCPopoverOpen}
                tocState={tocProps}
                style={floatingStyles}
              />
            </ClickOutsideHandler>
          );
        }

        return (
          <div
            ref={tocRef}
            className={classNames(styles.container, {
              [styles.isShowingTOC]: isTOCExpanded,
              [styles.hasBanner]: hasBanner,
              [styles.isPublicView]: isPublicView,
              [styles.tablet]: postLayoutSize === 'tablet',
              [styles.monitor]: postLayoutSize === 'monitor',
            })}
          >
            <div
              onMouseEnter={() => setIsTOCPopoverOpen(true)}
              onMouseLeave={() => setIsTOCPopoverOpen(false)}
              className={styles.actionButtons}
            >
              <TocButton
                ref={refs.setReference}
                onClick={() => {
                  if (hasHeaders && canTOCExpand) {
                    toggleTOC();
                  }
                }}
                ariaLabel={tocButtonAriaLabel}
                hasHeaders={hasHeaders}
              />

              {hasHeaders && !canTOCExpand && (
                <TOCPopOver
                  ref={refs.setFloating}
                  open={isTOCPopoverOpen}
                  tocState={tocProps}
                  style={floatingStyles}
                />
              )}
            </div>
            {canTOCExpand && (
              <motion.nav
                key="toc"
                initial={false}
                animate={{
                  opacity: isTOCExpanded ? 1 : 0,
                  x: isTOCExpanded ? '0%' : '-50%',
                }}
                transition={{ type: 'tween', duration: 0.15 }}
                className={styles.tocContainer}
                ref={tocContainerRef}
              >
                <h4 className={styles.title}>
                  Table of Contents
                  <Button
                    className={styles.closeAreaButton}
                    aria-label={tocButtonAriaLabel}
                    onClick={() => toggleTOC()}
                  />
                </h4>
                <ScrollableContentContainer>
                  <TableOfContents {...tocProps} />
                </ScrollableContentContainer>
              </motion.nav>
            )}

            {isTOCExpanded && (
              <Button
                className={styles.closeAreaButton}
                aria-label={tocButtonAriaLabel}
                onClick={() => toggleTOC()}
              />
            )}
          </div>
        );
      }}
    </ConnectedTableOfContents>
  );
};

export default PostStickyControl;
